import { PageOptions } from '@graphcommerce/framer-next-pages'
import { HygraphPagesQuery, hygraphPageContent } from '@graphcommerce/graphcms-ui'
import { StoreConfigDocument, redirectOrNotFound } from '@graphcommerce/magento-store'
import {
  PageMeta,
  MetaRobots,
  GetStaticProps,
  LayoutTitle,
  useStorefrontConfig,
} from '@graphcommerce/next-ui'
import { Box, Container, Typography } from '@mui/material'
import { GetStaticPaths } from 'next'
import {
  HobbyList,
  HobbyListDocument,
  HobbyListQuery,
  HobbyPathsDocument,
  LayoutHeaderVaessen,
  LayoutNavigation,
  LayoutNavigationProps,
  RowRenderer,
} from '../../components'
import { RowBannerSlider } from '../../components/GraphCMS/RowBannerSlider/RowBannerSlider'
import { RowProductList } from '../../components/GraphCMS/RowProductList/RowProductList'
import { RowProductListFragment } from '../../components/GraphCMS/RowProductList/RowProductList.gql'
import { setTitle, setTitleClean } from '../../components/GraphCMS/utils'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { GetProductsBySkuQuery } from '../../graphql/GetProductsBySku.gql'
import { getProductsBySku } from '../../lib/get-products-by-sku/get-products-by-sku'
import { graphqlSsrClient, graphqlSharedClient } from '../../lib/graphql/graphqlSsrClient'

type Props = HygraphPagesQuery & HobbyListQuery & { rowProductListData: GetProductsBySkuQuery[] }
type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

function HobbyPage(props: Props) {
  const { pages, hobbyPosts, rowProductListData } = props
  const page = pages[0]
  const title = page.title ?? ''
  const titleClean = setTitleClean(title)
  const storeB2B = useStorefrontConfig().magentoStoreCode.includes('b2b')

  const metaTitle = page.metaTitle ?? titleClean
  const metaDescription = page?.metaDescription ?? ''
  const metaRobots =
    (page?.metaRobots.toLowerCase().split('_').flat(1) as MetaRobots[]) ?? undefined
  const canonical = page?.url ? `/${page.url}` : undefined

  let filteredPage = { ...page }
  let { content } = filteredPage

  // if the first content type is a RowBannerSlider we actually want to show it full width and outside of the normal content renderer
  const heroBanner = content[0].__typename === 'RowBannerSlider' ? content[0] : null
  if (heroBanner) {
    content = content.slice(1, content.length)
  }

  // update our filterdPage with the modified content, let the RowRenderer take care of the rest
  filteredPage = {
    ...filteredPage,
    content,
  }

  let rowProductListIndex = 0

  return (
    <>
      <PageMeta
        title={metaTitle}
        metaDescription={metaDescription}
        metaRobots={storeB2B ? ['noindex', 'nofollow'] : metaRobots}
        canonical={canonical}
      />
      <LayoutHeaderVaessen floatingMd>
        <LayoutTitle component='span' size='small'>
          {titleClean}
        </LayoutTitle>
      </LayoutHeaderVaessen>
      {heroBanner && (
        <RowBannerSlider
          identity={heroBanner?.identity}
          bannerSliderSlides={heroBanner?.bannerSliderSlides}
          sx={(theme) => ({ marginBottom: theme.spacings.lg })}
        />
      )}
      <Container maxWidth='xl'>
        <Box sx={(theme) => ({ mt: theme.spacings.xl, mb: theme.spacings.md })}>
          <Typography
            component='h1'
            variant='h1'
            align='center'
            dangerouslySetInnerHTML={{ __html: setTitle(title) }}
          />
        </Box>
      </Container>
      <RowRenderer
        {...filteredPage}
        renderer={{
          RowProductList: (rowProps) => {
            rowProductListIndex++
            return (
              <RowProductList
                {...rowProps}
                productsBySku={rowProductListData[rowProductListIndex - 1].productsBySku}
              />
            )
          },
        }}
      />
      <Container>
        <Typography
          component='h2'
          variant='h1'
          align='center'
          dangerouslySetInnerHTML={{ __html: setTitle('LET US [[inspire]] YOU') }}
        />
      </Container>
      <HobbyList hobbyPosts={hobbyPosts} />
    </>
  )
}

HobbyPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default HobbyPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  return { paths: [], fallback: 'blocking' } // no SSG for now!
  if (import.meta.graphCommerce.limitSsg) return { paths: [], fallback: 'blocking' }

  const responses = locales.map(async (locale) => {
    const staticClient = graphqlSsrClient(locale)
    const hobbyPaths = staticClient.query({ query: HobbyPathsDocument })
    const { pages } = (await hobbyPaths).data
    return (
      pages.map((page) => ({ params: { url: `${page?.url}`.replace('hobby/', '') }, locale })) ?? []
    )
  })
  const paths = (await Promise.all(responses)).flat(1)
  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ locale, params }) => {
  const urlKey = params?.url ?? '??'
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)
  const limit = 3
  const conf = client.query({ query: StoreConfigDocument })
  const page = hygraphPageContent(staticClient, `hobby/${urlKey}`)

  const hobbyPosts = staticClient.query({
    query: HobbyListDocument,
    variables: { currentUrl: [`hobby/${urlKey}`], first: limit },
  })
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  if (!(await page).data.pages?.[0]) {
    return redirectOrNotFound(staticClient, conf, { url: `hobby/${urlKey}` }, locale)
  }

  const singlePage = (await page).data.pages?.[0]

  const rowProductListInstancesSkus = singlePage.content
    .filter((instance) => instance.__typename === 'RowProductList')
    .map((instance) => (instance as RowProductListFragment).skUs)

  const rowProductListData: GetProductsBySkuQuery[] = []
  for (const skus of rowProductListInstancesSkus) {
    // eslint-disable-next-line no-await-in-loop
    const productData = await getProductsBySku(staticClient, skus.replaceAll(' ', '').split(','))
    rowProductListData.push(productData)
  }

  return {
    props: {
      ...(await page).data,
      ...(await hobbyPosts).data,
      ...(await layout).data,
      rowProductListData,
      up: { href: '/hobby', title: 'Hobbies' },
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 20,
  }
}
